import { IItinerarymap, V2 } from '@eventr/common'
import { ColoursObj, Icon, Loading, Typography } from '@eventr/common-storybook'
import { Checkbox, Divider, Stack, Switch } from '@mui/material'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { AccordionTransition } from '../../../common/AccordionTransition'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { NavigationView } from '../../layout/NavigationView'

export default function LocalNotificationsPage() {
    const { notificationPermissionsGranted } = useBoundStore(
        useShallow((s) => {
            return {
                notificationPermissionsGranted:
                    s.notificationPermissionsGranted,
            }
        })
    )

    useEffect(() => {
        bs().syncNotificationPermissions()
    }, [])

    return (
        <NavigationView
            navigationBarTitle={i18n.t('account.local-notifications')}
            backButtonAction={() => {
                window.pushPath('/account/settings')
            }}
            usePageTransition
            navigationBarTrailing={[
                {
                    id: 'moreButtonEditItinerary',
                    icon: 'More',
                    popoverItems: [
                        {
                            title: i18n.t('account.pending'),
                            action: () => {
                                window.pushPath(
                                    '/account/local-notifications/pending'
                                )
                            },
                        },
                    ],
                },
            ]}
        >
            {notificationPermissionsGranted ? (
                <GrantedPermissionsView />
            ) : (
                <DeniedPermissionsView />
            )}
        </NavigationView>
    )
}

function GrantedPermissionsView() {
    const {
        scheduleLocalNotifications,
        setScheduleLocalNotifications,
        itineraryMap,
    } = useBoundStore(
        useShallow((s) => {
            return {
                scheduleLocalNotifications: s.scheduleLocalNotifications,
                setScheduleLocalNotifications: s.setScheduleLocalNotifications,
                itineraryMap: s.itineraryMap,
            }
        })
    )

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setScheduleLocalNotifications(event.target.checked)
    }

    return (
        <Stack>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                padding="16px"
            >
                <Typography>{i18n.t('account.allow-notifications')}</Typography>

                <Switch
                    checked={scheduleLocalNotifications}
                    onChange={handleChange}
                />
            </Stack>

            <Divider />

            {scheduleLocalNotifications && (
                <Stack padding="16px" gap="16px">
                    <Typography variant="bodyBold">
                        {i18n.t('account.customise-notifications')}
                    </Typography>

                    {itineraryMap
                        ?.filter(
                            (i) => i.end_time * 1000 >= new Date().getTime()
                        )
                        .sort((a, b) => b.start_time - a.start_time)
                        .map((itinerary) => (
                            <ItineraryNotificationsRow
                                key={itinerary.id}
                                itinerary={itinerary}
                            />
                        ))}
                </Stack>
            )}
        </Stack>
    )
}

function ItineraryNotificationsRow({
    itinerary,
}: {
    itinerary: IItinerarymap
}) {
    const { itineraryData, notificationFilters } = useBoundStore(
        useShallow((s) => {
            return {
                itineraryData: s.itineraryData,
                notificationFilters: s.notificationFilters,
            }
        })
    )
    const itineraryInNotificationFilter = !Object.keys(
        notificationFilters
    ).includes(itinerary.id.toString())

    const indeterminate = (() => {
        const hiddenSections = notificationFilters[itinerary.id]

        if (!hiddenSections) return false

        const allSections = itineraryData[itinerary.id]?.v2
            ?.map((s) => s.sectionID)
            .filter((i) => i !== 1) ?? []

        const isEqual =
            hiddenSections.length === allSections.length &&
            hiddenSections.every((sectionId) => allSections.includes(sectionId))

        return !isEqual
    })()

    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)

    const data = itineraryData[itinerary.id]
    const isInFuture = itinerary.end_time * 1000 >= new Date().getTime()

    const handleClick = async () => {
        setLoading(true)
        await bs().pullEventData(itinerary.id)
        setLoading(false)
    }

    return (
        <Stack>
            <Stack
                justifyContent="space-between"
                direction="row"
                alignItems="center"
                bgcolor={ColoursObj.backgrounds.secondary}
                borderRadius="8px"
                padding="8px"
            >
                <Stack direction="row" alignItems="center" gap="16px">
                    <Stack width="24px" height="24px">
                        {isInFuture && data && (
                            <Checkbox
                                checked={
                                    itineraryInNotificationFilter ||
                                    indeterminate
                                }
                                indeterminate={indeterminate}
                                onChange={() =>
                                    bs().notificationFilterToggleItinerary(
                                        itinerary.id,
                                        data.v2
                                            ?.map((s) => s.sectionID)
                                            .filter((i) => i !== 1) ?? []
                                    )
                                }
                                style={{
                                    height: '24px',
                                    width: '24px',
                                }}
                            ></Checkbox>
                        )}
                    </Stack>

                    <Typography
                        variant="bodyBold"
                        color={
                            data && isInFuture
                                ? ColoursObj.font.primary
                                : ColoursObj.font.secondary
                        }
                    >
                        {itinerary.itinerary_name}
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" gap="8px">
                    {isInFuture &&
                        (!data ? (
                            loading ? (
                                <Loading />
                            ) : (
                                <Icon
                                    name="Download"
                                    iconColour={ColoursObj.core.secondary}
                                    onClick={handleClick}
                                />
                            )
                        ) : (
                            <Icon
                                name={open ? 'Chevron_Up' : 'Chevron_Down'}
                                onClick={() => {
                                    setOpen(!open)
                                }}
                            ></Icon>
                        ))}
                </Stack>
            </Stack>

            <AccordionTransition open={open && isInFuture}>
                <Stack gap="16px" paddingTop="16px">
                    {data?.v2
                        ?.filter((v2) => v2.sectionID != 1)
                        ?.map((v2) => (
                            <SectionInItineraryNotificationsRow
                                key={v2.sectionID}
                                itinerary={itinerary}
                                v2={v2}
                            />
                        ))}
                </Stack>
            </AccordionTransition>
        </Stack>
    )
}

function SectionInItineraryNotificationsRow({
    itinerary,
    v2,
}: {
    itinerary: IItinerarymap
    v2: V2
}) {
    const { notificationFilters } = useBoundStore(
        useShallow((s) => ({
            notificationFilters: s.notificationFilters,
        }))
    )

    const isChecked = !(
        notificationFilters[itinerary.id]?.includes(v2.sectionID) ?? false
    )

    return (
        <Stack direction="row" alignItems="center" paddingX="8px" gap="16px">
            <Checkbox
                checked={isChecked}
                onChange={() =>
                    bs().notificationFilterToggleSection(
                        v2.sectionID,
                        itinerary.id
                    )
                }
                style={{
                    height: '24px',
                    width: '24px',
                }}
            />

            <Typography>{v2.sectionDisplayName}</Typography>
        </Stack>
    )
}

function DeniedPermissionsView() {
    return (
        <Stack gap="16px">
            <Typography>
                Allow EventR to send you notifications in your device settings.
            </Typography>
        </Stack>
    )
}
